import React, { useState } from "react";
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton,
  Drawer, List, ListItem, ListItemText, ListItemIcon,
  Divider, Menu, MenuItem, useTheme, useMediaQuery,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import AppsIcon from "@mui/icons-material/Apps";
import BusinessIcon from "@mui/icons-material/Business";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FavoriteIcon from "@mui/icons-material/Favorite";
import WaterIcon from "@mui/icons-material/Water";
import ExploreIcon from "@mui/icons-material/Explore";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import LayersIcon from "@mui/icons-material/Layers";
import { PRODUCTS } from "../data/products";

const NAV_ICONS = {
  amyloidconnect: <FavoriteIcon />,
  nephrotrax: <WaterIcon />,
  compass: <ExploreIcon />,
  resourceclan: <VolunteerActivismIcon />,
};

export default function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsAnchor, setProductsAnchor] = useState(null);

  const productsOpen = Boolean(productsAnchor);

  const handleProductsClick = (e) => setProductsAnchor(e.currentTarget);
  const handleProductsClose = () => setProductsAnchor(null);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
    handleProductsClose();
  };

  const drawer = (
    <Box sx={{ width: 280 }}>
      {/* Drawer Header */}
      <Box sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        p: 0, background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)", color: "white",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          <Box sx={{ height: 64, width: 64, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, p: 1.5 }}>
            <Box component="img" src="/logo.png" alt="VeraInsight" sx={{ height: "100%", width: "100%", objectFit: "contain" }} />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", ml: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>VeraInsight Healthcare</Typography>
            <Typography variant="caption" sx={{ fontStyle: "italic", opacity: 0.9, fontSize: "0.65rem" }}>Where Insight Meets Care</Typography>
          </Box>
        </Box>
        <IconButton onClick={() => setMobileOpen(false)} sx={{ color: "white", mr: 1 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <List>
        {[
          { label: "Home",      path: "/",          icon: <HomeIcon /> },
          { label: "About",     path: "/about",      icon: <InfoIcon /> },
          { label: "Products",  path: "/products",   icon: <AppsIcon /> },
          { label: "Platforms", path: "/platforms",  icon: <LayersIcon /> },
          { label: "Pharma Partners", path: "/pharma", icon: <BusinessIcon /> },
          { label: "Services",  path: "/services",   icon: <MedicalServicesIcon /> },
          { label: "Solutions", path: "/solutions",  icon: <BusinessIcon /> },
          { label: "Contact",   path: "/contact",    icon: <ContactMailIcon /> },
        ].map((item) => (
          <ListItem
            button key={item.path}
            onClick={() => handleNavigation(item.path)}
            sx={{ "&:hover": { bgcolor: "rgba(26,35,126,0.08)" } }}
          >
            <ListItemIcon sx={{ color: "#1a237e" }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}

        <Divider />

        <Box sx={{ px: 2, py: 1, bgcolor: "rgba(0,0,0,0.02)" }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 1 }}>
            Products
          </Typography>
        </Box>

        {PRODUCTS.map((product) => (
          <ListItem
            button
            key={product.key}
            onClick={() => handleNavigation(product.path)}
            sx={{ "&:hover": { bgcolor: "rgba(26,35,126,0.08)" } }}
          >
            <ListItemIcon sx={{ color: product.color }}>{NAV_ICONS[product.key]}</ListItemIcon>
            <ListItemText
              primary={product.name}
              secondary={product.status === "Live" ? product.tagline : product.status}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" elevation={2}
        sx={{ background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo + Brand */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0, flexGrow: isMobile ? 1 : 0, mr: isMobile ? 0 : 4 }}>
            <Box component={RouterLink} to="/"
              sx={{ height: 64, width: 64, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, p: 1, transition: "all 0.3s ease", "&:hover": { transform: "scale(1.1)" } }}>
              <Box component="img" src="/logo.png" alt="VeraInsight Logo" sx={{ height: "100%", width: "100%", objectFit: "contain" }} />
            </Box>
            <Box component={RouterLink} to="/" sx={{ color: "white", textDecoration: "none", display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" noWrap sx={{ fontWeight: 700, fontSize: { xs: "1.1rem", md: "1.3rem" }, letterSpacing: 0.5, lineHeight: 1.2 }}>
                VeraInsight Healthcare
              </Typography>
              <Typography variant="caption" sx={{ fontStyle: "italic", fontSize: { xs: "0.65rem", md: "0.75rem" }, opacity: 0.9, letterSpacing: 0.3, mt: -0.5 }}>
                Where Insight Meets Care
              </Typography>
            </Box>
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1, display: "flex", gap: 1, justifyContent: "flex-end" }}>
                <Button color="inherit" component={RouterLink} to="/" sx={{ fontWeight: 500 }}>Home</Button>
                <Button color="inherit" component={RouterLink} to="/about" sx={{ fontWeight: 500 }}>About</Button>

                {/* Products Dropdown */}
                <Button color="inherit" onClick={handleProductsClick} endIcon={<ArrowDropDownIcon />} sx={{ fontWeight: 500 }}>
                  Products
                </Button>
                <Menu anchorEl={productsAnchor} open={productsOpen} onClose={handleProductsClose}
                  PaperProps={{ elevation: 3, sx: { mt: 1.5, minWidth: 280, borderRadius: 2 } }}>
                  {PRODUCTS.map((product) => (
                    <MenuItem key={product.key} onClick={() => handleNavigation(product.path)} sx={{ py: 1.5 }}>
                      <Box sx={{ mr: 1.5, color: product.color, display: "flex" }}>{NAV_ICONS[product.key]}</Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.status === "Live" ? product.tagline : `${product.status} — ${product.tagline}`}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>

                {/* ── Platforms link ── */}
                <Button color="inherit" component={RouterLink} to="/platforms" sx={{ fontWeight: 500 }}>
                  Platforms
                </Button>

                {/* ── Pharma Partners link ── */}
                <Button color="inherit" component={RouterLink} to="/pharma"
                  sx={{ fontWeight: 500, bgcolor: "rgba(244,132,95,0.15)", borderRadius: 1,
                    "&:hover": { bgcolor: "rgba(244,132,95,0.25)" } }}>
                  Pharma Partners
                </Button>

                <Button color="inherit" component={RouterLink} to="/services" sx={{ fontWeight: 500 }}>Services</Button>
                <Button color="inherit" component={RouterLink} to="/solutions" sx={{ fontWeight: 500 }}>Solutions</Button>
                <Button color="inherit" component={RouterLink} to="/contact" sx={{ fontWeight: 500 }}>
                  Contact
                </Button>
              </Box>

              {/* CTA Button */}
              <Button variant="contained" component={RouterLink} to="/products"
                sx={{ bgcolor: "#f9a825", color: "#1a237e", fontWeight: 700, px: 3, ml: 2, "&:hover": { bgcolor: "#f57f17" } }}>
                Our Products
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer variant="temporary" anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }} sx={{ "& .MuiDrawer-paper": { width: 280 } }}>
        {drawer}
      </Drawer>

      {/* Toolbar Spacer */}
      <Toolbar />
    </>
  );
}